import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

import formatOrderNumber from 'utils/formatOrderNumber';
import patientDobToAge from 'utils/patientDobToAge';

import OrderViewBarActions from './OrderViewBarActions';

const styles = theme => ({
  root: {
    width: 'auto',
    padding: '25px 0 5px',
    backgroundColor: theme.palette.common.white,
  },
  grid: {
    position: 'relative',
    width: 'auto',
    minHeight: 70,
    margin: '-4px 26px 4px',
  },
  gridItem: {
    maxWidth: '16%',
  },
  specialState: {
    display: 'flex',
    marginTop: 3,
  },
  typography: {
    marginTop: 9,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  state: {
    display: 'inline-flex',
    flexDirection: 'column',
    justifyContent: 'center',
    height: 24,
    verticalAlign: 'top',
    margin: '-2px 5px 0 0',
  },
  stateIcon: {
    display: 'inline-block',
    width: 8,
    height: 8,
    margin: 2,
    borderRadius: '50%',
    overflow: 'hidden',
    textIndent: '100%',
  },
  stateIconCurrent: {
    backgroundColor: theme.palette.active['500'],
  },
  stateIconFuture: {
    backgroundColor: theme.palette.future['500'],
  },
});

const OrderViewBar = ({
  ID,
  Patient,
  classes,
  ...otherProps
}) => {
  const specialState = otherProps['Order Line']
    .map(item => item['Special State'])
    .reduce((accumulator, currentValue) => {
      if (accumulator === 'combined') {
        return accumulator;
      }
      if (accumulator !== currentValue) {
        return 'combined';
      }
      return accumulator;
    });
  return (
    <AppBar
      elevation={2}
      color="primary"
      position="static"
      className={classes.root}
    >
      <Grid
        className={classes.grid}
        container
        justify="space-between"
        spacing={16}
      >
        <Grid item>
          <Typography variant="title">
            {`Order: ${formatOrderNumber(ID)}`}
          </Typography>
          <div className={classes.specialState}>
            <div className={classes.state}>
              {['combined', 'current'].indexOf(specialState) !== -1 &&
                <div
                  className={classNames(classes.stateIcon, classes.stateIconCurrent)}
                />
              }
              {['combined', 'future'].indexOf(specialState) !== -1 &&
                <div
                  className={classNames(classes.stateIcon, classes.stateIconFuture)}
                />
              }
            </div>
            <Typography>
              {specialState === 'current' &&
                'Current'
              }
              {specialState === 'future' &&
                'Future'
              }
              {specialState === 'combined' &&
                'Current, Future'
              }
            </Typography>
          </div>
        </Grid>
        <Grid className={classes.gridItem} item>
          <Typography className={classes.typographyCaption} variant="caption">
            Payer
          </Typography>
          <Typography className={classes.typography}>
            {(otherProps['Primary Insurance']
              && otherProps['Primary Insurance']['Insurance Info']) ? (
                otherProps['Primary Insurance']['Insurance Info']['Payer Name']
              ) : (
                'Self Payed'
              )}
          </Typography>
        </Grid>
        <Grid className={classes.gridItem} item>
          <Typography className={classes.typographyCaption} variant="caption">
            Patient
          </Typography>
          <Typography className={classes.typography}>
            {`${Patient['Last Name']}` }     
            {`, ${Patient['First Name']}`}
            {Patient['Middle Name'] || ''}
            {`, ${Patient['Record Number']}`}
          </Typography>
        </Grid>
        <Grid className={classes.gridItem} item>
          <Typography className={classes.typographyCaption} variant="caption">
            DOB, Age
          </Typography>
          <Typography className={classes.typography}>
            {Patient['Date of Birth']},{' '}
            {patientDobToAge(Patient['Date of Birth'])}
          </Typography>
        </Grid>
        <Grid className={classes.gridItem} item>
          <Typography className={classes.typographyCaption} variant="caption">
            Gender
          </Typography>
          <Typography className={classes.typography}>
            {Patient.Gender}
          </Typography>
        </Grid>
        {Patient['Contact Information']['Primary Phone'] &&
          <Grid className={classes.gridItem} item>
            <Typography className={classes.typographyCaption} variant="caption">
              Contact
            </Typography>
            <Typography className={classes.typography}>
              {Patient['Contact Information']['Primary Phone']}
            </Typography>
          </Grid>
        }
        <Grid className={classes.gridItem} item>
          <Typography className={classes.typographyCaption} variant="caption">
            Created at
          </Typography>
          <Typography className={classes.typography}>
            {otherProps['Date of Creation']}
          </Typography>
        </Grid>
        <Grid item>
          <OrderViewBarActions
            ID={ID}
            Patient={Patient}
            {...otherProps}
          />
        </Grid>
      </Grid>
    </AppBar>
  );
};

OrderViewBar.propTypes = {
  ID: PropTypes.number.isRequired,
  'Primary Insurance': PropTypes.shape({
    'Insurance Info': PropTypes.shape({
      'Payer Name': PropTypes.string,
    }),
  }),
  Patient: PropTypes.shape({
    'Record Number': PropTypes.number.isRequired,
    'First Name': PropTypes.string.isRequired,
    'Last Name': PropTypes.string.isRequired,
    'Middle Name': PropTypes.string,
    'Date of Birth': PropTypes.string.isRequired,
    Gender: PropTypes.string.isRequired,
    'Contact Information': PropTypes.shape({
      'Primary Phone': PropTypes.string,
    }),
  }).isRequired,
  'Date of Creation': PropTypes.string.isRequired,
  'Order Line': PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number.isRequired,
    'Special State': PropTypes.oneOf(['current', 'future']),
  })).isRequired,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    grid: PropTypes.string.isRequired,
    gridItem: PropTypes.string.isRequired,
    specialState: PropTypes.string.isRequired,
    typography: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    stateIcon: PropTypes.string.isRequired,
    stateIconCurrent: PropTypes.string.isRequired,
    stateIconFuture: PropTypes.string.isRequired,
  }).isRequired,
};

OrderViewBar.defaultProps = {
  'Primary Insurance': null,
};

export default withStyles(styles)(OrderViewBar);



// WEBPACK FOOTER //
// ./src/components/workflow/OrderViewBar/OrderViewBar.js